import { SvgIconProps } from '@mui/material';
import { FC } from 'react';
import { Control } from 'react-hook-form';

import { AutocompleteLocationRecord } from '@abg/shared/@generated';
import { LocationVehicleCategory } from '@abg/shared/constants';
import { LocationSuggestion } from '@abg/typings';

import { UiTextData } from './molecules/location-details/location-details.types';
import { GroupLimits } from './molecules/suggestion-group/suggestion-group.types';

export type LocationSuggestionOption = Pick<
  LocationSuggestion,
  | 'siteCodeGroup'
  | 'locationName'
  | 'identifier'
  | 'addressLine1'
  | 'addressLine2'
  | 'cityName'
  | 'countyName'
  | 'countryName'
  | 'countyCode'
  | 'suggestionTypeCode'
  | 'siteCodeDescription'
  | 'typeCode'
  | 'mnemonic'
  | 'suggestionGroup'
  | 'holidays'
  | 'hoursOfOperation'
  | 'is24hoursDropOffAvailable'
  | 'isKeyDropLocation'
  | 'regionCode'
  | 'stateCode'
  | 'countryCode'
  | 'isoCountryCode'
  | 'phones'
  | 'postalCode'
  | 'relPath'
  | 'latitude'
  | 'longitude'
  | 'drivableDistance'
  | 'isFreePickup'
  | 'isTruck'
  | 'isSelfServiceKiosk'
  | 'locationVehicleCategory'
> & {
  isAvisFirstLocation?: boolean;
  // ARAF-9900: Avis First Lite sub-classification of an Avis First location.
  // Sourced from the /stations BFF transaction; see af-tier.ts for precedence.
  isAvisFirstLite?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
  deliveryAddress?: AutocompleteLocationRecord | any;
};

export type LocationSearchProperties = {
  loadCurrentOptions: (query: {
    latitude: number;
    longitude: number;
  }) => Promise<LocationSuggestionOption[] | undefined>;
  loadOptions: (
    searchTerm: string,
  ) => Promise<LocationSuggestionOption[] | undefined>;
  name: string;
  inputIcon?: FC<SvgIconProps>;
  control?: Control;
  errors: {
    incorrectValue: string;
    emptyValue: string;
  };
  allowGeolocation: boolean;
  uiTextData: UiTextData;
  testIdPrefix: string;
  optionTestIdBuilder?: (option: LocationSuggestionOption) => string;
  endAdornment?: React.JSX.Element;
  groupLimits?: GroupLimits;
  includeSavedLocations?: boolean;
  includeRecentLocations?: boolean;
  excludeVehicleCategories?: LocationVehicleCategory[];
  showLocationDetailsFeature?: boolean;
  showStationDirection?: boolean;
  onSelectLocation?: (location: LocationSuggestionOption) => void;
  onLocationSelected?: () => void;
  onInputChange?: (value: string) => void;
  currentReservationLocation?: LocationSuggestionOption | null;
  disabled?: boolean;
  shouldClearField?: boolean;
  label?: string;
  withLabel?: boolean;
  onClickOutside?: (options: { isEmptyInputValue?: boolean }) => void;
  /**
   * Callback fired when a city (location without mnemonic) is selected.
   * Used by BookingWidget to trigger form submission when a city is selected.
   */
  onCitySelect?: (location: LocationSuggestionOption) => void;
  isAFLocalMarketChangesEnabled?: boolean;
  isAFLocalMarketStepOneEnabled?: boolean;
  isAFLocalMarketStepOneOtherLocationLabelEnabled?: boolean;
  isAFLocalMarketStepTwoVLPChangesEnabled?: boolean;
  hideLocationIcon?: boolean;
};

export enum SuggestionOptionsMode {
  KeywordsOptions,
  GeoLocationRequested,
  GeoLocationOptions,
}
